import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { Box, DialogContent, Grid, Stack, Toolbar } from "@mui/material";
import styles from "./styles.module.scss";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { useGetImage } from "../../../../hooks/images";
import { ImageCache } from "../../../../models/ImageCache";
import { strings } from "../../../../localization/LocalizedStrings";
import { ContainedButton } from "../../../ContainedButton";
import { ILabel } from "../../../../hooks/labels";
import { ModifyLabelDialog } from "../ModifyLabelDialog";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { LazyImageAspectFit } from "../../../LazyImageAspectFit";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import { DialogState } from "../../../../models/DialogState";

export const LabelDialog: FunctionComponent<ILabelDialogProps> = ({
  label,
  isOpen,
  onClose,
  canEdit,
  onModifiedItem,
  onDeletedItem
}) => {
  const isMobile = useIsMobile();
  const [myLabel, setMyLabel] = useState(label);
  const { getImage } = useGetImage();
  const [image, setImage] = useState();
  const [thumbnail, setThumbnail] = useState(label.thumbnail);
  const [modifyDialogState, setModifyDialogState] = useState<DialogState<ILabel>>({
    mode: "closed"
  });
  const genericImage = new URL("../../../../images/generic_label.png", import.meta.url).href;
  useEffect(() => {
    setThumbnail(myLabel.thumbnail);
    fetchImage().then();
  }, [myLabel.uuid, myLabel.hasImage, myLabel.thumbnail]);
  useEffect(() => {
    setMyLabel(label);
  }, [label]);
  const currentLabelUuid = useRef("");
  const fetchImage = async () => {
    currentLabelUuid.current = myLabel.uuid;
    setImage(undefined);
    if (!myLabel.hasImage) return;
    const response = await getImage({
      name: myLabel.uuid,
      width: ImageCache.bigLabelImageSize,
      height: ImageCache.bigLabelImageSize
    });
    // Discard image if label changed
    if (currentLabelUuid.current != myLabel.uuid) return;
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  return (
    <PiperDialog fullWidth maxWidth={"sm"} open={isOpen} onClose={onClose}>
      <div className={styles.dialog}>
        <Toolbar>
          <h3>{myLabel.name}</h3>
          <Box flexGrow={1} />
          {canEdit && (
            <ToolbarButton
              onClick={() => setModifyDialogState({ mode: "edit", item: label })}
              tooltip={strings.label_edit}
              iconType={IconType.EditSquare}
            />
          )}
          <ToolbarButton onClick={onClose} tooltip={strings.close} iconType={IconType.Close} />
        </Toolbar>
      </div>
      <DialogContent dividers>
        {myLabel ? (
          <Stack spacing={2}>
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <LazyImageAspectFit
                className={styles.image}
                style={{ width: isMobile ? "100%" : "50%" }}
                imageStyle={{ borderRadius: "6%" }}
                image={image}
                thumbnail={thumbnail}
                defaultImage={genericImage}
                alt={myLabel.name}
              />
              <Stack style={{ width: isMobile ? "100%" : "50%" }}>
                <Grid container>
                  <Grid item xs className={styles.redirectGridItem}>
                    <ContainedButton
                      className={styles.redirectButton}
                      disabled={myLabel.discogsId?.length === 0}
                      href={`https://www.discogs.com/label/${myLabel.discogsId}`}
                      target={"_blank"}
                      component={"a"}
                    >
                      {strings.open_on_discogs}
                    </ContainedButton>
                  </Grid>
                  <Grid item xs className={styles.redirectGridItem}>
                    <ContainedButton
                      className={styles.redirectButton}
                      disabled={!myLabel.website || myLabel.website.length === 0}
                      href={myLabel.website}
                      target={"_blank"}
                    >
                      {strings.ma_website_button_title}
                    </ContainedButton>
                  </Grid>
                </Grid>
                <Box flexGrow={1} />
              </Stack>
            </Stack>
            <p className={styles.profile}>{myLabel.profile}</p>
          </Stack>
        ) : (
          <Box sx={{ height: 200 }} />
        )}
      </DialogContent>
      <ModifyLabelDialog
        key={modifyDialogState.item?.uuid}
        open={modifyDialogState.mode !== "closed"}
        onClose={() => setModifyDialogState({ mode: "closed", item: modifyDialogState.item })}
        label={modifyDialogState.item}
        originalImageData={image}
        onModifiedItem={newItem => {
          onModifiedItem?.(newItem);
          setMyLabel(newItem);
        }}
        onDeletedItem={onDeletedItem}
      />
    </PiperDialog>
  );
};

export interface ILabelDialogProps {
  label: ILabel;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
  onModifiedItem?: (label: ILabel) => void;
  onDeletedItem?: (label: ILabel) => void;
}
